import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  CalendarIcon,
  CogIcon,
  PencilSquareIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

export default function ParametresAdhesion() {
  const [settings, setSettings] = useState([]);
  const [serviceSettings, setServiceSettings] = useState({ miellerieActif: true, unafActif: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingService, setSavingService] = useState(null);
  const [activeTab, setActiveTab] = useState('SAR');

  // ── Tarifs miellerie (AMAIR) ──────────────────────────────────────────────
  const [miellerieTarifs, setMiellerieTarifs] = useState({});   // { [annee]: { cotisationBase, extraction, cire, candi, conditionnement } }
  const [editingMiellerie, setEditingMiellerie] = useState(null); // annee en cours d'édition
  const [miellerieTarifsForm, setMiellerieTarifsForm] = useState({}); // { [annee]: form values }
  const [savingMiellerie, setSavingMiellerie] = useState(false);
  const [miellerieSaveMsg, setMiellerieSaveMsg] = useState('');

  const { user } = useAuth();
  const navigate = useNavigate();

  const anneeActuelle = new Date().getFullYear();

  const fetchMiellerieTarifsForYear = useCallback(async (annee) => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/settings/miellerie-tarifs?annee=${annee}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMiellerieTarifs((prev) => ({ ...prev, [annee]: data.tarifs }));
    } catch (err) {
      console.error('Erreur chargement tarifs miellerie:', err);
    }
  }, []);

  useEffect(() => {
    if (!user || user.role !== 'super_admin') {
      navigate('/');
      return;
    }

    fetchSettings();
    fetchServiceSettings();
  }, [user, navigate]);

  // Charger les tarifs miellerie quand l'onglet AMAIR est actif
  useEffect(() => {
    if (activeTab !== 'AMAIR') return;
    const amairSettings = settings.filter((s) => s.organisme === 'AMAIR');
    amairSettings.forEach((s) => {
      if (miellerieTarifs[s.annee] === undefined) {
        fetchMiellerieTarifsForYear(s.annee);
      }
    });
  }, [activeTab, settings, fetchMiellerieTarifsForYear, miellerieTarifs]);

  const fetchServiceSettings = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/service-settings`);
      setServiceSettings(response.data);
    } catch (error) {
      console.error('Erreur lors de la récupération des paramètres de services:', error);
    }
  };

  const handleToggleService = async (service) => {
    setSavingService(service);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/service-settings/toggle/${service}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setServiceSettings(response.data);
    } catch (error) {
      console.error('Erreur lors de la modification:', error);
      alert(error.response?.data?.message || 'Erreur lors de la modification');
    } finally {
      setSavingService(null);
    }
  };

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      console.log('Fetching settings from API...');
      
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/settings/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Settings received:', response.data);
      
      // Les paramètres sont maintenant par organisme et par année
      if (response.data && response.data.length > 0) {
        setSettings(response.data);
      } else {
        console.warn('Aucun paramètre reçu du backend');
        setSettings([]);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des paramètres:', error);
      console.error('Error details:', error.response?.data || error.message);
      alert('Erreur lors du chargement des paramètres. Vérifiez la console.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAdhesions = async (organisme, annee) => {
    // Vérifier si c'est l'année en cours
    if (annee === anneeActuelle) {
      alert('Impossible de fermer les adhésions de l\'année en cours');
      return;
    }

    // Confirmation pour les années passées
    const isPast = annee < anneeActuelle;
    if (isPast) {
      const confirm = window.confirm(
        `Êtes-vous sûr de vouloir modifier le statut des adhésions pour l'année ${annee} (année passée) ?`
      );
      if (!confirm) return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/settings/${organisme}/${annee}/toggle-adhesions`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Rafraîchir les paramètres
      await fetchSettings();
      
      alert(response.data.message);
    } catch (error) {
      console.error('Erreur lors de la modification:', error);
      const errorMsg = error.response?.data?.message || 'Erreur lors de la modification des paramètres';
      alert(errorMsg);
    } finally {
      setSaving(false);
    }
  };


  const startEditMiellerie = (annee) => {
    const current = miellerieTarifs[annee] || {};
    setMiellerieTarifsForm((prev) => ({
      ...prev,
      [annee]: {
        cotisationBase: current.cotisationBase ?? '',
        extraction: current.extraction ?? '',
        cire: current.cire ?? '',
        candi: current.candi ?? '',
        conditionnement: current.conditionnement ?? 1,
      },
    }));
    setEditingMiellerie(annee);
    setMiellerieSaveMsg('');
  };

  const handleSaveMiellerieTarifs = async (annee) => {
    setSavingMiellerie(true);
    setMiellerieSaveMsg('');
    try {
      const token = localStorage.getItem('token');
      const form = miellerieTarifsForm[annee];
      const tarifs = {
        cotisationBase: Number(form.cotisationBase),
        extraction:     Number(form.extraction),
        cire:           Number(form.cire),
        candi:          Number(form.candi),
        conditionnement: Number(form.conditionnement),
      };
      const { data } = await axios.put(
        `${process.env.REACT_APP_API_URL}/settings/miellerie-tarifs`,
        { annee, tarifs },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMiellerieTarifs((prev) => ({ ...prev, [annee]: tarifs }));
      setEditingMiellerie(null);
      setMiellerieSaveMsg(data.message || 'Tarifs enregistrés');
      setTimeout(() => setMiellerieSaveMsg(''), 4000);
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur lors de la sauvegarde des tarifs');
    } finally {
      setSavingMiellerie(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full bg-gray-50">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-200 px-8 py-4">
        <div className="flex items-center text-sm text-gray-500">
          <span className="font-medium text-gray-900">Abeille Réunion</span>
          <svg className="mx-2 h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
          <span>Paramètres d'adhésion</span>
        </div>
      </div>

      {/* Header */}
      <div className="px-8 py-6">
        <h1 className="text-3xl font-bold text-gray-900">
          Paramètres d'adhésion et services
        </h1>
        <p className="mt-2 text-sm text-gray-600">
          Gérez l'ouverture des adhésions et l'activation des services pour tous les groupements
        </p>
      </div>

      {/* Onglets */}
      <div className="px-8">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            {['SAR', 'AMAIR', 'ADAR'].map((organisme) => (
              <button
                key={organisme}
                onClick={() => setActiveTab(organisme)}
                className={`${
                  activeTab === organisme
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
              >
                <span className="flex items-center space-x-2">
                  <span>{organisme}</span>
                </span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="px-8 pb-8 pt-6">
        <div className="space-y-8">
          {/* Afficher uniquement le groupement actif */}
          {[activeTab].map((organisme) => {
            const organismeSettings = settings.filter(s => s.organisme === organisme);
            if (organismeSettings.length === 0) return null;
            
            return (
              <div key={organisme} className="space-y-4">
                {/* En-tête du groupement */}
                <div className="flex items-center space-x-3">
                  <h2 className="text-2xl font-bold text-gray-900">{organisme}</h2>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    organisme === 'SAR' ? 'bg-honey-100 text-honey-800' :
                    organisme === 'AMAIR' ? 'bg-green-100 text-green-800' :
                    'bg-blue-100 text-blue-800'
                  }`}>
                    {organisme === 'SAR' ? 'Syndicat Apicole de La Réunion' :
                     organisme === 'AMAIR' ? 'Association de la Maison de l\'Apiculture de l\'Île de La Réunion' :
                     'Association pour le Développement de l\'Apiculture à l\'île de la Réunion'}
                  </span>
                </div>

                {/* Paramètres d'adhésion par année */}
                <div className="space-y-4">
                  {organismeSettings.map((setting) => {
            const isPast = setting.annee < anneeActuelle;
            const isCurrent = setting.annee === anneeActuelle;
            const isFuture = setting.annee > anneeActuelle;

                    return (
                      <div key={setting.annee} className="bg-white rounded-lg shadow overflow-hidden">
                        {/* Header de la card */}
                        <div className={`px-6 py-4 border-b border-gray-200 ${
                          isCurrent ? 'bg-indigo-50' : 'bg-gray-50'
                        }`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <CalendarIcon className={`h-6 w-6 ${
                                isCurrent ? 'text-indigo-600' : 'text-gray-400'
                              }`} />
                              <div className="flex items-center">
                                <h3 className="text-lg font-bold text-gray-900">
                                  Année {setting.annee}
                                </h3>
                                {isCurrent && (
                                  <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-600 text-white">
                                    En cours
                                  </span>
                                )}
                                {isPast && (
                                  <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-400 text-white">
                                    Passée
                                  </span>
                                )}
                                {isFuture && (
                                  <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-600 text-white">
                                    À venir
                                  </span>
                                )}
                                {setting.dateOuverture && (
                                  <p className="text-sm text-gray-500 mt-1">
                                    Ouvert le {new Date(setting.dateOuverture).toLocaleDateString('fr-FR')}
                                  </p>
                                )}
                              </div>
                            </div>

                            {/* Toggle adhésions */}
                            <div className="flex items-center space-x-3">
                              <span className="text-sm font-medium text-gray-700">
                                Adhésions {setting.adhesionsOuvertes ? 'ouvertes' : 'fermées'}
                              </span>
                              <button
                                onClick={() => handleToggleAdhesions(setting.organisme, setting.annee)}
                                disabled={saving || isCurrent}
                                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                                  setting.adhesionsOuvertes ? 'bg-indigo-600' : 'bg-gray-200'
                                } ${(saving || isCurrent) ? 'opacity-50 cursor-not-allowed' : ''}`}
                              >
                                <span
                                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                    setting.adhesionsOuvertes ? 'translate-x-5' : 'translate-x-0'
                                  }`}
                                />
                              </button>
                              {isCurrent && (
                                <span className="text-xs text-gray-500">(année en cours toujours ouverte)</span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* ── Tarifs miellerie (AMAIR seulement) ── */}
                        {organisme === 'AMAIR' && (() => {
                          const tarifs = miellerieTarifs[setting.annee];
                          const isEditingThis = editingMiellerie === setting.annee;
                          const form = miellerieTarifsForm[setting.annee] ?? {};

                          const CHAMPS = [
                            { key: 'cotisationBase', label: 'Cotisation annuelle', unit: '€ / an' },
                            { key: 'extraction',     label: 'Extraction des hausses', unit: '€ / hausse' },
                            { key: 'cire',           label: 'Gaufrage de cire',    unit: '€ / kg' },
                            { key: 'candi',          label: 'Fabrication de candi', unit: '€ / sac 25 kg' },
                            { key: 'conditionnement', label: 'Conditionnement de miel', unit: '€ / tranche de 5 kg' },
                          ];

                          return (
                            <div className="border-t border-gray-100">
                              <div className="px-6 py-3 bg-honey-50/40 flex items-center justify-between gap-3">
                                <div className="flex items-center gap-2">
                                  <span className="text-xl">🍯</span>
                                  <span className="text-sm font-semibold text-honey-900">Tarifs miellerie {setting.annee}</span>
                                  {!tarifs && !isEditingThis && (
                                    <span className="text-xs text-slate-400 italic">— aucun tarif défini pour cette année</span>
                                  )}
                                </div>
                                {!isEditingThis ? (
                                  <button
                                    type="button"
                                    onClick={() => startEditMiellerie(setting.annee)}
                                    className="inline-flex items-center gap-1.5 text-xs font-medium text-honey-700 hover:text-honey-900 px-2.5 py-1.5 rounded-md hover:bg-honey-100 transition-colors"
                                  >
                                    <PencilSquareIcon className="h-3.5 w-3.5" />
                                    Modifier
                                  </button>
                                ) : (
                                  <div className="flex items-center gap-2">
                                    <button
                                      type="button"
                                      onClick={() => handleSaveMiellerieTarifs(setting.annee)}
                                      disabled={savingMiellerie}
                                      className="inline-flex items-center gap-1 text-xs font-medium text-white bg-honey-600 hover:bg-honey-700 disabled:opacity-50 px-2.5 py-1.5 rounded-md transition-colors"
                                    >
                                      <CheckIcon className="h-3.5 w-3.5" />
                                      {savingMiellerie ? 'Enregistrement…' : 'Enregistrer'}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setEditingMiellerie(null)}
                                      disabled={savingMiellerie}
                                      className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-slate-800 px-2.5 py-1.5 rounded-md hover:bg-slate-100 transition-colors"
                                    >
                                      <XMarkIcon className="h-3.5 w-3.5" />
                                      Annuler
                                    </button>
                                  </div>
                                )}
                              </div>

                              <div className="px-6 pb-4 pt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                                {CHAMPS.map(({ key, label, unit }) => (
                                  <div key={key}>
                                    <p className="text-xs font-medium text-slate-500 mb-1">{label}</p>
                                    {isEditingThis ? (
                                      <div className="flex items-center gap-1">
                                        <input
                                          type="number"
                                          min="0"
                                          step="0.01"
                                          value={form[key] ?? ''}
                                          onChange={(e) =>
                                            setMiellerieTarifsForm((prev) => ({
                                              ...prev,
                                              [setting.annee]: { ...prev[setting.annee], [key]: e.target.value },
                                            }))
                                          }
                                          className="w-20 px-2 py-1 text-sm border border-honey-300 rounded-md focus:ring-1 focus:ring-honey-500 focus:border-honey-500"
                                        />
                                        <span className="text-xs text-slate-400 whitespace-nowrap">{unit.replace(/^€ \/ /, '')}</span>
                                      </div>
                                    ) : (
                                      <p className="text-sm font-semibold text-slate-900">
                                        {tarifs?.[key] != null && tarifs[key] !== ''
                                          ? `${Number(tarifs[key]).toFixed(2)} €`
                                          : '—'}
                                        <span className="ml-1 text-xs font-normal text-slate-400">/ {unit.replace(/^€ \/ /, '')}</span>
                                      </p>
                                    )}
                                  </div>
                                ))}
                              </div>

                              {miellerieSaveMsg && editingMiellerie === null && (
                                <div className="px-6 pb-3">
                                  <p className="text-xs text-green-700 font-medium">{miellerieSaveMsg}</p>
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {/* Info automatique */}
                        {isFuture && (
                          <div className="px-6 py-3 bg-yellow-50 border-t border-yellow-100">
                            <div className="flex items-start">
                              <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <div className="ml-3">
                                <p className="text-sm text-yellow-700">
                                  Les adhésions pour l'année N+1 se fermeront automatiquement au 1er janvier {setting.annee + 1}.
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Services pour ce groupement */}
                {organisme === 'AMAIR' && (
                  <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                      <div className="flex items-center">
                        <CogIcon className="h-5 w-5 mr-2 text-gray-600" />
                        <h3 className="text-base font-medium text-gray-900">Services AMAIR</h3>
                      </div>
                    </div>
                    <div className="px-6 py-4 flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-honey-100 flex items-center justify-center mr-4">
                          <span className="text-xl">🍯</span>
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-gray-900">Services de la miellerie</h4>
                          <p className="text-sm text-gray-500">Accès à la miellerie collective</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className={`text-sm font-medium ${serviceSettings.miellerieActif ? 'text-green-600' : 'text-gray-500'}`}>
                          {serviceSettings.miellerieActif ? 'Actif' : 'Désactivé'}
                        </span>
                        <button
                          onClick={() => handleToggleService('miellerie')}
                          disabled={savingService === 'miellerie'}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-honey-500 focus:ring-offset-2 ${
                            serviceSettings.miellerieActif ? 'bg-honey-500' : 'bg-gray-200'
                          } ${savingService === 'miellerie' ? 'opacity-50 cursor-wait' : ''}`}
                        >
                          <span
                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              serviceSettings.miellerieActif ? 'translate-x-5' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {organisme === 'SAR' && (
                  <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                      <div className="flex items-center">
                        <CogIcon className="h-5 w-5 mr-2 text-gray-600" />
                        <h3 className="text-base font-medium text-gray-900">Services SAR</h3>
                      </div>
                    </div>
                    <div className="px-6 py-4 flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center mr-4">
                          <span className="text-xl">🐝</span>
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-gray-900">Services de l'UNAF</h4>
                          <p className="text-sm text-gray-500">Assurance ruches via l'UNAF</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className={`text-sm font-medium ${serviceSettings.unafActif ? 'text-green-600' : 'text-gray-500'}`}>
                          {serviceSettings.unafActif ? 'Actif' : 'Désactivé'}
                        </span>
                        <button
                          onClick={() => handleToggleService('unaf')}
                          disabled={savingService === 'unaf'}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                            serviceSettings.unafActif ? 'bg-indigo-600' : 'bg-gray-200'
                          } ${savingService === 'unaf' ? 'opacity-50 cursor-wait' : ''}`}
                        >
                          <span
                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              serviceSettings.unafActif ? 'translate-x-5' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>


        {/* Info générale */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">Informations importantes</h3>
              <div className="mt-2 text-sm text-blue-700">
                <ul className="list-disc list-inside space-y-1">
                  <li>Les adhésions de l'année en cours sont toujours ouvertes</li>
                  <li>Vous pouvez ouvrir/fermer les adhésions pour l'année N+1</li>
                  <li>Les tarifs sont modifiables par l'administrateur du site</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
